// API client. Handles JSON, the CSRF double-submit header, and 401 → logout.

const CSRF_STORAGE_KEY = 'wms.csrf';
const sessionExpiredListeners = new Set<() => void>();

export function onSessionExpired(listener: () => void): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

function notifySessionExpired(status: number, code: string | null): void {
  if (status === 401 && code === 'invalid_session') {
    setCsrfToken(null);
    for (const listener of sessionExpiredListeners) listener();
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly body: unknown,
  ) {
    super(`API error ${status} (${code})`);
  }
}

export type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Json;
  signal?: AbortSignal;
}

export function setCsrfToken(token: string | null): void {
  if (token) sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  else sessionStorage.removeItem(CSRF_STORAGE_KEY);
}

export function getCsrfToken(): string | null {
  return sessionStorage.getItem(CSRF_STORAGE_KEY);
}

export async function api<T>(
  path: string,
  opts: RequestOptions = {},
  retriedCsrf = false,
): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  // CSRF double-submit on writes — see SessionGuard.
  if (method !== 'GET') {
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  const text = await res.text();
  const body: unknown = text ? safeParse(text) : null;

  if (!res.ok) {
    const code = extractCode(body) ?? `http_${res.status}`;
    notifySessionExpired(res.status, code);
    // Self-heal a stale CSRF token. The token lives in per-tab sessionStorage
    // while the session cookie is cross-tab and longer-lived, so a re-login in
    // another tab leaves an older tab holding a stale token — every write then
    // 403s with `csrf_mismatch`. Refresh from /auth/me and retry the write once.
    // Safe against duplicates: the guard rejects before the handler runs, so no
    // side effect occurred on the rejected request.
    if (res.status === 403 && code === 'csrf_mismatch' && method !== 'GET' && !retriedCsrf) {
      if (await refreshCsrf()) {
        return api<T>(path, opts, true);
      }
    }
    throw new ApiError(res.status, code, body);
  }

  return body as T;
}

/**
 * Re-fetch the CSRF token for the current session cookie from /auth/me and
 * store it. Returns false when there's no valid session to refresh from.
 */
async function refreshCsrf(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/me', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      notifySessionExpired(res.status, extractCode(safeParse(await res.text())));
      return false;
    }
    const body = (await res.json()) as { csrfToken?: unknown };
    if (typeof body.csrfToken === 'string' && body.csrfToken) {
      setCsrfToken(body.csrfToken);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractCode(body: unknown): string | null {
  if (body && typeof body === 'object' && 'message' in body) {
    const m = (body as { message?: unknown }).message;
    if (m && typeof m === 'object' && 'code' in m) {
      const code = (m as { code?: unknown }).code;
      if (typeof code === 'string') return code;
    }
  }
  if (body && typeof body === 'object' && 'code' in body) {
    const c = (body as { code?: unknown }).code;
    if (typeof c === 'string') return c;
  }
  return null;
}
